'use client'

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import { useThreatFilters } from './useThreatFilters'
import ThreatFilterPanel from '@/components/commons/ThreatFilterPanel/ThreatFilterPanel'
import PageHeader from '@/src/components/commons/generics/PageHeader/PageHeader'
import Image from 'next/image'
import moment from 'moment'
import LandingPageContainer from '@/src/components/commons/generics/LandingPageContainer/LandingPageContainer'
import { ThreatDataResult, FilterState } from './ThreatLibrary.interface'
import styles from './ThreatLibraryPage.module.scss'
import IconAlert from '../../../public/assets/images/icon_triangle_warning-red.svg'
import IconAngleRightRed from '../../../public/assets/images/icon_angle_right_red.svg'
import Link from 'next/link'

// --- Configuration ---
const RESULTS_PER_PAGE = 20
/** Fixed origin + path allowlist so filter values only become query params (SSRF-safe). */
const THREAT_LIBRARY_ORIGIN = 'https://threatlibrary.zscaler.com'
const THREAT_LIBRARY_API = {
  threats: '/api/threats',
  threatTotals: '/api/threat-totals',
} as const

function threatLibraryRequestUrl(
  path: (typeof THREAT_LIBRARY_API)[keyof typeof THREAT_LIBRARY_API],
  query: Record<string, string>,
): URL {
  const url = new URL(path, THREAT_LIBRARY_ORIGIN)
  url.search = new URLSearchParams(query).toString()
  return url
}

/** Only for URLs from {@link threatLibraryRequestUrl} (fixed origin + allowlisted path). */
async function threatLibraryFetch(url: URL, init?: RequestInit): Promise<Response> {
  if (url.origin !== THREAT_LIBRARY_ORIGIN) {
    throw new Error('Threat library request blocked: unexpected origin')
  }
  // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf -- URL from threatLibraryRequestUrl only (fixed origin + allowlisted path); Semgrep cannot model that.
  return globalThis.fetch(url, { cache: 'no-store', ...init })
}

const EMPTY_THREAT_DATA: ThreatDataResult[] = []

const formatLastUpdated = (dateString: string) => {
  return moment(dateString).format('MMMM DD, YYYY')
}

const truncateDescription = (text: string, maxLength: number = 200): string => {
  if (!text || text.length <= maxLength) {
    return text
  }
  return text.substring(0, maxLength).trim() + '...'
}

const ARRAY_FILTER_KEYS = ['category', 'severity', 'direction', 'engines', 'product', 'azListing']
const getInitialFiltersFromUrl = (searchParams: URLSearchParams): Partial<FilterState> => {
  const initialFilters: Partial<FilterState> = {}
  searchParams.forEach((value, key) => {
    const isArrayField = ARRAY_FILTER_KEYS.includes(key)
    const filterKey = key as keyof FilterState
    if (isArrayField) {
      const values = value.split(',').filter((v) => v)
      if (values.length > 0) {
        initialFilters[filterKey] = values as FilterState[typeof filterKey]
      }
    } else {
      if (value) {
        initialFilters[filterKey] = value as FilterState[typeof filterKey]
      }
    }
  })
  return initialFilters
}

const FILTER_DEBOUNCE_MS = 300

// API FETCH FUNCTIONS
const fetchThreatTotals = async (
  params: Record<string, string | number>,
  init?: RequestInit,
): Promise<number> => {
  const paramsWithoutPage = { ...params }
  delete paramsWithoutPage.page
  const query = Object.fromEntries(
    Object.entries(paramsWithoutPage).map(([k, v]) => [k, String(v)]),
  ) as Record<string, string>
  const url = threatLibraryRequestUrl(THREAT_LIBRARY_API.threatTotals, query)

  try {
    const response = await threatLibraryFetch(url, init)
    if (!response.ok) return 0
    const data = await response.json()
    return Number(data.results || data.count || 0)
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error
    console.log('fetchThreatTotals error: ', error)
    return 0
  }
}

const fetchThreatLibraryData = async (
  params: Record<string, string | number>,
  init?: RequestInit,
): Promise<ThreatDataResult[]> => {
  const query = Object.fromEntries(
    Object.entries(params).map(([k, v]) => [k, String(v)]),
  ) as Record<string, string>
  const url = threatLibraryRequestUrl(THREAT_LIBRARY_API.threats, query)

  try {
    const response = await threatLibraryFetch(url, init)
    if (!response.ok) throw new Error('Failed to fetch threat data from API.')
    const data = await response.json()
    return data.results || []
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error
    console.log('fetchThreatLibraryData error: ', error)
    return []
  }
}

// =====================================================================
// MAIN CLIENT COMPONENT
// =====================================================================

const ThreatLibraryClient: React.FC = () => {
  const [isMounted, setIsMounted] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [isTotalsPending, setIsTotalsPending] = useState(true)
  const [apiData, setApiData] = useState<ThreatDataResult[]>([])
  const [totalResults, setTotalResults] = useState(0)

  const searchParams = useSearchParams()
  const router = useRouter()

  useEffect(() => {
    setIsMounted(true)
  }, [])

  // --- GET INITIAL STATE FROM URL ---
  const initialFilters = useMemo(() => {
    return getInitialFiltersFromUrl(searchParams)
  }, [searchParams])

  const { filters, updateFilter, resetFilters, getApiParams, isPanelOpen, setIsPanelOpen } =
    useThreatFilters(EMPTY_THREAT_DATA, initialFilters)

  /** Stable key for "which query" ignoring page — used to skip debounce on pagination-only changes. */
  const filterQueryKey = useMemo(
    () => JSON.stringify(getApiParams(filters, 1)),
    [filters, getApiParams],
  )
  const lastCommittedFilterKeyRef = useRef<string>('')

  // Handlers to manage filter state AND page reset together
  const handleFilterChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: keyof FilterState, value: any) => {
      updateFilter(key, value)
      setCurrentPage(1) // Reset page on any filter change
    },
    [updateFilter],
  )

  const handleResetFilters = useCallback(() => {
    resetFilters()
    setCurrentPage(1) // Reset page on full reset
  }, [resetFilters])

  // =====================================================================
  // API SYNCHRONIZATION AND DEBOUNCE LOGIC
  // =====================================================================

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller

    const paginationOnly =
      lastCommittedFilterKeyRef.current === filterQueryKey &&
      lastCommittedFilterKeyRef.current !== ''
    const delayMs = paginationOnly ? 0 : FILTER_DEBOUNCE_MS

    const handler = setTimeout(() => {
      const run = async () => {
        setIsLoading(true)
        if (!paginationOnly) {
          setIsTotalsPending(true)
        }
        const params = getApiParams(filters, currentPage)

        try {
          const results = await fetchThreatLibraryData(params, { signal })
          if (signal.aborted) return
          setApiData(results)
          lastCommittedFilterKeyRef.current = filterQueryKey
        } catch (error) {
          if (signal.aborted || (error as Error).name === 'AbortError') return
          console.log('fetchData error: ', error)
          setApiData([])
          setTotalResults(0)
          setIsTotalsPending(false)
          return
        } finally {
          if (!signal.aborted) {
            setIsLoading(false)
          }
        }

        if (signal.aborted) return

        fetchThreatTotals(params, { signal })
          .then((count) => {
            if (!signal.aborted) {
              setTotalResults(count)
              setIsTotalsPending(false)
            }
          })
          .catch((err) => {
            if (signal.aborted || (err as Error).name === 'AbortError') return
            console.log('fetchThreatTotals follow-up error: ', err)
            if (!signal.aborted) {
              setTotalResults(0)
              setIsTotalsPending(false)
            }
          })
      }

      void run()
    }, delayMs)

    return () => {
      clearTimeout(handler)
      controller.abort()
    }
  }, [filterQueryKey, currentPage, getApiParams])

  // synchronize state to URL
  useEffect(() => {
    if (!isMounted) return

    const params = getApiParams(filters, currentPage)
    const newQueryParams = new URLSearchParams(params as Record<string, string>).toString()

    router.replace(`?${newQueryParams}`, { scroll: false })
  }, [filters, currentPage, isMounted, getApiParams, router])

  // =====================================================================
  // PAGINATION LOGIC
  // =====================================================================

  const totalPages = Math.ceil(totalResults / RESULTS_PER_PAGE)
  const currentResults = apiData

  const resultsRangeStart = totalResults > 0 ? (currentPage - 1) * RESULTS_PER_PAGE + 1 : 0
  /** Prefer the actual page payload when present — `/threat-totals` can disagree slightly or lag vs `/threats`. */
  const resultsRangeEnd =
    totalResults > 0
      ? !isLoading && currentResults.length > 0
        ? resultsRangeStart + currentResults.length - 1
        : Math.min(currentPage * RESULTS_PER_PAGE, totalResults)
      : 0
  const resultsGrandTotal = Math.max(totalResults, resultsRangeEnd)

  // Reset page number logic (if filtering makes the current page invalid)
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    } else if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1)
    }
  }, [totalPages, currentPage])

  // =====================================================================
  // UI COMPONENTS
  // =====================================================================

  const ThreatCard: React.FC<{ threat: ThreatDataResult }> = ({ threat }) => (
    <div className={styles.threatCard}>
      <div className={`${styles.threatHeader} mb-2.5`}>
        <h2 className={`${styles.threatName} `}>
          <Image
            src={IconAlert.src}
            alt="Alert"
            width={24}
            height={24}
            className="inline-block! align-middle mr-2.5"
          />
          <Link
            href={`/threat-library/${threat.universalID}`}
            className="text-[22px] font-medium text-white!"
          >
            {threat.threatName}
          </Link>
        </h2>
        <p className={styles.lastUpdated}>
          Last Updated: {formatLastUpdated(threat.dateLastModified)}
        </p>
      </div>
      <p className={styles.description}>{truncateDescription(threat.description)}</p>
      <p className={styles.classiciations}>
        <span>Engine: {threat.engine}</span>
        <span>Product: {threat.product}</span>
      </p>
      {threat.ref1 && (
        <p className={styles.references}>
          <span>Reference: </span>
          <a href={threat.ref1} className={styles.refLink} target="_blank">
            Blog Post
          </a>
          {threat.ref2 && (
            <a href={threat.ref2} className={`${styles.refLink} ml-2.5`} target="_blank">
              Blog Post
            </a>
          )}
        </p>
      )}
    </div>
  )

  const PaginationControls: React.FC = () => {
    if (totalPages <= 1) return null

    const handlePageChange = (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }

    const handlePageKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>, page: number) => {
      if (e.key !== 'Enter' && e.key !== ' ') {
        return
      }
      e.preventDefault()
      handlePageChange(page)
    }

    const getDisplayPages = () => {
      const pages: (number | string)[] = []
      const delta = 2
      const left = currentPage - delta
      const right = currentPage + delta

      pages.push(1)

      if (left > 2) {
        pages.push('...')
      }

      for (let i = 2; i <= totalPages; i++) {
        if (i >= left && i <= right && i < totalPages) {
          pages.push(i)
        }
      }

      if (right < totalPages - 1) {
        if (pages[pages.length - 1] !== '...') {
          pages.push('...')
        }
      }

      if (totalPages > 1 && !pages.includes(totalPages)) {
        pages.push(totalPages)
      }

      return pages.filter((item, index, array) => {
        return item !== '...' || array[index - 1] !== '...'
      })
    }

    const displayPages = getDisplayPages()
    const formatNumber = (num: number) => num.toLocaleString()

    return (
      <div className={styles.paginationControls}>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`${styles.pageButton}`}
        >
          <Image
            src={IconAngleRightRed.src}
            alt="Previous Result Page"
            width={6}
            height={11}
            className={`inline-block! align-middle ${styles.prevPage}`}
          />
        </button>

        {displayPages.map((page, index) =>
          page === '...' ? (
            <span key={`dot-${index}`} className={styles.ellipsis} aria-hidden="true">
              ...
            </span>
          ) : (
            <span
              key={page}
              role="button"
              tabIndex={0}
              aria-label={`Go to page ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
              onClick={() => handlePageChange(page as number)}
              onKeyDown={(e) => handlePageKeyDown(e, page as number)}
              className={page === currentPage ? styles.activePage : styles.pageNumber}
            >
              {formatNumber(page as number)}
            </span>
          ),
        )}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={styles.pageButton}
        >
          <Image
            src={IconAngleRightRed.src}
            alt="Next Result Page"
            width={6}
            height={11}
            className={`inline-block! align-middle ${styles.nextPage}`}
          />
        </button>
      </div>
    )
  }

  // =====================================================================
  // RENDER
  // =====================================================================

  const pageHeading = 'Threat Library'
  const pageSubHeading =
    'Browse the Zscaler ThreatLabz listing of threats. Use the search library to find specific threat information.'

  return (
    <>
      <LandingPageContainer className={`${styles.pageWrapper}`}>
        {/* Mobile Filter Panel Instance */}
        {isMounted && (
          <div
            className={`${styles.mobilePanelWrapper} ${isPanelOpen ? styles.mobilePanelOpen : ''}`}
          >
            <button
              className={styles.closeButton}
              onClick={() => setIsPanelOpen(false)}
              aria-label="Close filter panel"
            >
              ×
            </button>
            <ThreatFilterPanel
              isMobile={true}
              filters={filters}
              updateFilter={handleFilterChange}
              resetFilters={handleResetFilters}
              isPanelOpen={isPanelOpen}
              setIsPanelOpen={setIsPanelOpen}
              resultsCount={totalResults}
            />
          </div>
        )}
        <div className={`mx-auto relative`}>
          <div className={styles.header}>
            <PageHeader
              heading={pageHeading}
              subheading={pageSubHeading}
              className="text-left! text-white!"
            />
            {/* MOBILE TOGGLE BUTTON */}
            <button
              className={`${styles.mobileFilterButton} md:hidden!`}
              onClick={() => setIsPanelOpen(true)}
            >
              Filters
            </button>
          </div>

          <div className={styles.mainContent}>
            {/* DESKTOP FILTER PANEL INSTANCE */}
            <div className={styles.desktopSidebarWrapper}>
              <ThreatFilterPanel
                isMobile={false}
                filters={filters}
                updateFilter={handleFilterChange}
                resetFilters={handleResetFilters}
                isPanelOpen={false}
                setIsPanelOpen={setIsPanelOpen}
                resultsCount={totalResults}
              />
            </div>

            {/* Right Side: Results */}
            <div className={styles.resultsArea}>
              <div className={styles.resultsSummary}>
                {isTotalsPending ? (
                  <>Search Results: ...pending</>
                ) : totalResults === 0 ? (
                  <>Search Results: 0 of 0</>
                ) : (
                  <>
                    Search Results: {resultsRangeStart.toLocaleString()}-
                    {resultsRangeEnd.toLocaleString()} of {resultsGrandTotal.toLocaleString()}
                  </>
                )}
              </div>

              <div className={styles.threatList}>
                {currentResults.map((threat) => (
                  <ThreatCard key={threat.universalID} threat={threat} />
                ))}

                {totalResults === 0 && !isLoading && (
                  <p className="py-5">No threats found matching your criteria.</p>
                )}
                {totalResults > 0 && currentResults.length === 0 && !isLoading && (
                  <p className="py-5">No threats found for this page range.</p>
                )}
                {isLoading && <p className="py-5">Fetching data...</p>}
              </div>

              {/* Pagination Controls */}
              <PaginationControls />
            </div>
          </div>
        </div>
      </LandingPageContainer>
    </>
  )
}

export default ThreatLibraryClient
